import { Product, StoriesSettings } from '../types';
import { normalizeCategoryFolder } from './categoryFolders';

/**
 * Returns API endpoint URLs for the hosting server.
 * Uses clean relative URLs so all requests go directly to the hosting server where the site is deployed.
 */
export function getHostingApiEndpoints(endpoint: string): string[] {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return [cleanEndpoint];
}

/**
 * Converts a Blob URL to a Data URL if needed
 */
async function blobToDataUrl(url: string): Promise<string | null> {
  if (!url || !url.startsWith('blob:')) return null;
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Automatically uploads a product image (base64 or blob) directly to hosting /api/upload.php
 * and returns the permanent web-accessible URL (e.g. /images/phone-cases/my_case.webp)
 */
export async function uploadProductImageToHosting(
  imageSource: string | File,
  category: string,
  preferredFilename?: string
): Promise<string | null> {
  const cleanCategory = normalizeCategoryFolder(category);

  // If already a clean server path (e.g. /images/phone-cases/xxx.webp) or external HTTP URL, no upload needed
  if (typeof imageSource === 'string') {
    if (imageSource.startsWith('/images/') || (imageSource.startsWith('http') && !imageSource.startsWith('blob:'))) {
      return imageSource;
    }
  }

  let base64Payload: string | null = null;
  let filename = preferredFilename || `prod_${Date.now()}`;

  if (imageSource instanceof File) {
    filename = imageSource.name || filename;
    try {
      base64Payload = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(imageSource);
      });
    } catch {
      return null;
    }
  } else if (typeof imageSource === 'string') {
    if (imageSource.startsWith('data:image/')) {
      base64Payload = imageSource;
    } else if (imageSource.startsWith('blob:')) {
      base64Payload = await blobToDataUrl(imageSource);
    }
  }

  if (!base64Payload) {
    return typeof imageSource === 'string' ? imageSource : null;
  }

  const endpoints = getHostingApiEndpoints('/api/upload.php');
  
  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: cleanCategory,
          catalog: cleanCategory,
          filename: filename.endsWith('.webp') ? filename : `${filename}.webp`,
          image: base64Payload
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data && data.success && data.url) {
          return data.url;
        }
      }
    } catch (err) {
      console.warn(`[Auto-Upload] Upload error via ${endpoint}:`, err);
    }
  }

  return typeof imageSource === 'string' ? imageSource : null;
}

/**
 * Automatically saves a single product directly to the hosting MySQL database.
 */
export async function autoSaveProductToHosting(
  product: Product,
  currentProducts: Product[]
): Promise<{ success: boolean; message: string; updatedProduct: Product }> {
  let prodCopy = { ...product };

  // 1. If product image is a temporary blob or base64, automatically upload it first
  if (prodCopy.image && (prodCopy.image.startsWith('data:image/') || prodCopy.image.startsWith('blob:'))) {
    const cleanUrl = await uploadProductImageToHosting(
      prodCopy.image,
      prodCopy.category,
      `${prodCopy.slug || prodCopy.id}_main`
    );
    if (cleanUrl) {
      prodCopy.image = cleanUrl;
    }
  }

  // 2. Also sanitize any additional images
  if (Array.isArray(prodCopy.additionalImages) && prodCopy.additionalImages.length > 0) {
    const updatedAdditionals: string[] = [];
    for (let idx = 0; idx < prodCopy.additionalImages.length; idx++) {
      const addl = prodCopy.additionalImages[idx];
      if (addl && (addl.startsWith('data:image/') || addl.startsWith('blob:'))) {
        const cleanAddl = await uploadProductImageToHosting(
          addl,
          prodCopy.category,
          `${prodCopy.slug || prodCopy.id}_extra_${idx + 1}`
        );
        updatedAdditionals.push(cleanAddl || addl);
      } else {
        updatedAdditionals.push(addl);
      }
    }
    prodCopy.additionalImages = updatedAdditionals;
  }

  // 3. Merge with current catalog to form the authoritative list
  const exists = currentProducts.some(p => p.id === prodCopy.id);
  const updatedCatalog = exists
    ? currentProducts.map(p => p.id === prodCopy.id ? prodCopy : p)
    : [prodCopy, ...currentProducts];

  // 4. Send save request to hosting database endpoint
  const endpoints = getHostingApiEndpoints('/api/products.php');
  let saved = false;
  let responseMsg = '';

  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'sync',
          single_product: prodCopy,
          products: updatedCatalog
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data && data.success) {
          saved = true;
          responseMsg = data.message || `Product "${prodCopy.title}" saved to hosting database.`;
          break;
        }
      }
    } catch (err) {
      console.warn(`[Auto-Save] Error calling ${endpoint}:`, err);
    }
  }

  return {
    success: saved,
    message: saved ? responseMsg : 'Saved to browser and queued for hosting database save.',
    updatedProduct: prodCopy
  };
}

/**
 * Automatically syncs the catalog to the hosting database (e.g. after deletion, edit, or reordering)
 */
export async function autoSyncCatalogToHosting(
  products: Product[]
): Promise<{ success: boolean; count: number; message: string }> {
  const endpoints = getHostingApiEndpoints('/api/products.php');
  let saved = false;
  let count = products.length;
  let message = '';

  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'sync',
          products
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data && data.success) {
          saved = true;
          count = data.count !== undefined ? data.count : products.length;
          message = data.message || `Synchronized ${count} products to hosting database.`;
          break;
        }
      }
    } catch (err) {
      console.warn(`[Auto-Sync] Error connecting to ${endpoint}:`, err);
    }
  }

  return {
    success: saved,
    count,
    message: saved ? message : 'Saved locally and queued for hosting database synchronization.'
  };
}

/**
 * Uploads a story image to the hosting server via /api/upload.php under category 'stories'
 */
export async function uploadStoryImageToHosting(
  imageSource: string | File,
  storyId: string,
  suffix = 'slide'
): Promise<string | null> {
  return uploadProductImageToHosting(imageSource, 'stories', `story_${storyId}_${suffix}`);
}

/**
 * Saves the entire stories configuration and images to the hosting server/database
 */
export async function saveStoriesToHosting(
  storiesSettings: StoriesSettings
): Promise<{ success: boolean; message: string; storiesSettings?: StoriesSettings }> {
  const endpoints = getHostingApiEndpoints('/api/stories.php');
  let saved = false;
  let responseMsg = '';
  let updatedSettings: StoriesSettings | undefined = undefined;

  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storiesSettings })
      });

      if (res.ok) {
        const data = await res.json();
        if (data && data.success) {
          saved = true;
          responseMsg = data.message || 'Stories saved to database.';
          updatedSettings = data.storiesSettings || storiesSettings;
          break;
        }
      }
    } catch (err) {
      console.warn(`[Auto-Save-Stories] Error calling ${endpoint}:`, err);
    }
  }

  return {
    success: saved,
    message: saved ? responseMsg : 'Saved to browser IndexedDB and queued for hosting database save.',
    storiesSettings: updatedSettings
  };
}

/**
 * Fetches authoritative stories from the hosting server / MySQL / JSON
 */
export async function fetchStoriesFromHosting(): Promise<StoriesSettings | null> {
  const endpoints = getHostingApiEndpoints('/api/stories.php');

  for (const endpoint of endpoints) {
    try {
      const res = await fetch(`${endpoint}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Accept': 'application/json' }
      });

      if (res.ok) {
        const data = await res.json();
        if (data && data.success && data.storiesSettings) {
          return data.storiesSettings;
        }
      }
    } catch (err) {
      console.warn(`[Fetch-Stories] Error calling ${endpoint}:`, err);
    }
  }

  return null;
}
